import { MenuList, PaperProps } from '@mui/material';
import { Editor } from '@tiptap/core';
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHotkeys } from '../hooks/useHotkeys';
import { CustomPopover } from './CustomPopover';

type CustomMenuProps = {
  totalItems: number;
  onClose: () => void;
  children: (opts: { selectedIndex: number }) => ReactNode;
  selectItem: (index: number) => void;
  editor: Editor;
  clientRect?: (() => DOMRect | null) | null;
  paperProps?: PaperProps;
};

export const CustomMenu = ({
  children,
  editor,
  clientRect,
  totalItems,
  selectItem,
  onClose,
  paperProps,
}: CustomMenuProps) => {
  const commandListContainer = useRef<HTMLUListElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useHotkeys(
    editor,
    {
      ArrowUp: (e) => {
        setSelectedIndex((selectedIndex + totalItems - 1) % totalItems);
        e.preventDefault();
      },
      ArrowDown: (e) => {
        setSelectedIndex((selectedIndex + 1) % totalItems);
        e.preventDefault();
      },
      Enter: (e) => {
        selectItem(selectedIndex);
        e.stopPropagation();
        e.preventDefault();
      },
      Escape: () => {
        onClose();
      },
    },
    [totalItems, selectedIndex, setSelectedIndex, selectItem],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setSelectedIndex(0);
  }, [totalItems]);

  useLayoutEffect(() => {
    const container = commandListContainer?.current;

    const item = container?.children[selectedIndex] as HTMLElement;

    if (item && container) item.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  return (
    <CustomPopover
      editor={editor}
      clientRect={clientRect}
      paperProps={
        paperProps || {
          sx: {
            maxHeight: '330px',
            width: '280px',
            overflowY: 'auto',
            backgroundColor: (theme) => theme.palette.background.default,
          },
        }
      }
    >
      <MenuList ref={commandListContainer}>
        {children({ selectedIndex })}
      </MenuList>
    </CustomPopover>
  );
};
